import axios from "axios";
import config from "../config";
import * as url from "../helpers/url_helper";

const { api } = config;

export interface ClientSiteAttachmentItem {
  id: string;
  tenantId: string;
  tenantName?: string;
  clientSiteId: string;
  clientSiteName?: string;
  clientId: string;
  clientName?: string;
  fileName: string;
  filePath: string;
  fileSizeBytes: number;
  isDeleted: boolean;
}

export interface ClientSiteAttachmentResponse {
  items: ClientSiteAttachmentItem[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface ClientSiteAttachmentCreatePayload {
  tenantId: string;
  clientSiteId: string;
  clientId: string;
  attachment: File;
}

export interface ClientSiteAttachmentApiResponse {
  id: string;
  isSuccess: boolean;
  notFound: boolean;
  message: string;
  modelErrors: any[];
  result: ClientSiteAttachmentItem;
}

// Get auth token from Redux Persist
const getAuthToken = () => {
  try {
    const persistedState = sessionStorage.getItem('persist:root');
    if (persistedState) {
      const parsed = JSON.parse(persistedState);
      if (parsed.Login) {
        const loginState = JSON.parse(parsed.Login);
        if (loginState.user && Object.keys(loginState.user).length > 0) {
          return loginState.user.token || null;
        }
      }
    }
  } catch (e) {
    console.error("Error reading auth token from Redux Persist:", e);
  }
  return null;
};

// Get all attachments for a client site
export const getClientSiteAttachments = async (
  clientSiteId: string,
  pageNumber: number = 1,
  pageSize: number = 100
): Promise<ClientSiteAttachmentResponse> => {
  try {
    const token = getAuthToken();
    const response = await axios.get(`${api.API_URL}${url.GET_CLIENT_SITE_ATTACHMENTS}/${clientSiteId}`, {
      params: {
        pageNumber,
        pageSize,
      },
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json;odata.metadata=minimal;odata.streaming=true",
      },
    });
    
    return response as unknown as ClientSiteAttachmentResponse;
  } catch (error) {
    return {
      items: [],
      pageNumber,
      pageSize,
      totalCount: 0,
      totalPages: 0,
      hasPreviousPage: false,
      hasNextPage: false,
    };
  }
};

// Upload a new attachment
export const uploadClientSiteAttachment = async (
  payload: ClientSiteAttachmentCreatePayload
): Promise<ClientSiteAttachmentItem> => {
  const token = getAuthToken();
  const formData = new FormData();
  formData.append("TenantId", payload.tenantId);
  formData.append("ClientSiteId", payload.clientSiteId);
  formData.append("ClientId", payload.clientId);
  formData.append("Attachment", payload.attachment);

  const response = await axios.post<ClientSiteAttachmentApiResponse>(
    `${api.API_URL}${url.ADD_CLIENT_SITE_ATTACHMENT}`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json;odata.metadata=minimal;odata.streaming=true",
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data.result;
};

// Delete an attachment
export const deleteClientSiteAttachment = async (attachmentId: string): Promise<void> => {
  const token = getAuthToken();
  await axios.delete(`${api.API_URL}${url.DELETE_CLIENT_SITE_ATTACHMENT}/${attachmentId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json;odata.metadata=minimal;odata.streaming=true",
    },
  });
};

// Download attachment file - uses direct filePath URL
export const downloadClientSiteAttachment = async (
  attachment: ClientSiteAttachmentItem
): Promise<Blob> => {
  const token = getAuthToken();
  const response = await axios.get(attachment.filePath, {
    responseType: "blob",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

